import {
  ApplicationConfig,
  provideZonelessChangeDetection,
  APP_ID,
  importProvidersFrom
} from "@angular/core";
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withFetch
} from "@angular/common/http";
import { provideConfig } from "./social-auth-config";
import { environment } from "../environments/environment";
import { WindowRef } from "./plan-contribution/WindowRef";
import { WindowRefSub } from "./subscription-plan/WindowRefSub";
import { WindowRefSubscription } from "./subscription-plan-page/WindowRefSubscription";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule
} from "@angular/material/dialog";
import { MessageService } from "./message.service";
import { SocialLoginModule } from "@abacritt/angularx-social-login";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { SharedService } from "./shared.service";
import { CanonService } from "./canon.service";
import { ArticleService } from "./article.service";
import { CatagoryServiceService } from "./catagory-service.service";
import { UserServiceService } from "./user-service.service";
import { LoginService } from "./login.service";
import {
  Title,
  BrowserModule,
  provideClientHydration,
  withHttpTransferCacheOptions
} from "@angular/platform-browser";
import { DataService } from "./data.service";
import { AdService } from "./dynamic/ad.service";
import { FirebaseServiceService } from "./firebase-service.service";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { CommonModule, DATE_PIPE_DEFAULT_OPTIONS } from "@angular/common";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { VgCoreModule } from "@videogular/ngx-videogular/core";
import { VgControlsModule } from "@videogular/ngx-videogular/controls";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { VgOverlayPlayModule } from "@videogular/ngx-videogular/overlay-play";
import { MatIconModule } from "@angular/material/icon";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { MatSidenavModule } from "@angular/material/sidenav";
import { VgBufferingModule } from "@videogular/ngx-videogular/buffering";
import { MatCardModule } from "@angular/material/card";
import { MatListModule } from "@angular/material/list";
import { MatRadioModule } from "@angular/material/radio";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatSelectModule } from "@angular/material/select";
import { MatStepperModule } from "@angular/material/stepper";
import { CONST_ROUTING } from "./app.routing";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
// PdfViewerModule import removed from root config (SSR-safe) — see below. (#52)
import { MatTabsModule } from "@angular/material/tabs";
import { MatChipsModule } from "@angular/material/chips";
import { MatExpansionModule } from "@angular/material/expansion";
import { ServiceWorkerModule } from "@angular/service-worker";
import { RouterModule } from "@angular/router";
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireDatabaseModule } from "@angular/fire/compat/database";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatNativeDateModule } from "@angular/material/core";

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      AngularEditorModule,
      CommonModule,
      MatCheckboxModule,
      VgCoreModule,
      VgControlsModule,
      MatGridListModule,
      MatProgressSpinnerModule,
      VgOverlayPlayModule,
      MatIconModule,
      DragDropModule,
      MatSidenavModule,
      VgBufferingModule,
      MatCardModule,
      MatListModule,
      MatGridListModule,
      MatRadioModule,
      MatFormFieldModule,
      MatDatepickerModule,
      MatProgressBarModule,
      MatInputModule,
      MatMenuModule,
      MatButtonModule,
      FormsModule,
      ReactiveFormsModule,
      MatDialogModule,
      MatSelectModule,
      MatStepperModule,
      CONST_ROUTING,
      BrowserAnimationsModule,
      // PdfViewerModule removed from root — pdfjs-dist touches window/document at import,
      // crashing SSR route-extraction. Consuming components/lazy modules import it themselves. (#52)
      MatTabsModule,
      MatChipsModule,
      MatIconModule,
      MatExpansionModule,
      ServiceWorkerModule.register("/ngsw-worker-mod.js", {
        enabled: environment.enableServiceWorker
      }),
      RouterModule,
      // Firebase (AngularFireModule/Database) moved to browser-only firebaseProviders
      // (see bottom of file) so the firebase app is never created on the server —
      // @angular/fire compat eagerly inits browser-only Auth otherwise, crashing SSR. (#52)
      ServiceWorkerModule.register("/ngsw-worker.js", {
        enabled: environment.enableServiceWorker
      }), // for database
      NgxMatSelectSearchModule,
      MatToolbarModule,
      MatNativeDateModule,
      SocialLoginModule,
      MatExpansionModule
    ),
    HttpClient,
    WindowRef,
    WindowRefSub,
    WindowRefSubscription,
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: [] },
    MessageService,
    {
      provide: "SocialAuthServiceConfig",
      useFactory: provideConfig
    },
    // AngularFireAuth → browser-only firebaseProviders (server gets an inert stub). (#52)
    SharedService,
    CanonService,
    ArticleService,
    CatagoryServiceService,
    UserServiceService,
    LoginService,
    Title,
    DataService,
    AdService,
    // FirebaseServiceService → browser-only firebaseProviders (server gets an inert stub). (#52)
    MatDatepickerModule,
    { provide: APP_ID, useValue: "trd" },
    // Pin every `| date` pipe to IST (+0530) so the SERVER (runs in UTC) and the
    // BROWSER (IST) render the SAME time. Without this, SSR shows e.g. 11:02 AM (UTC)
    // and hydration re-renders it as 4:32 PM (IST) → the visible date "flip" on load.
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: "+0530" } },
    provideZonelessChangeDetection(),
    // ⚠️ DOM HYDRATION DISABLED (2026-07-28). These legacy templates contain invalid
    // HTML nesting the browser normalizes differently than the SSR string, so Angular's
    // hydration walker hits a non-element node and throws
    //   `TypeError: rNode.hasAttribute is not a function` (hasSkipHydrationAttrOnRElement)
    // which ABORTS client bootstrap → dead links (router never attaches) + stuck/wrong
    // theme on reload (reported on /podcast, /insights, intermittently home). The bad
    // nesting is spread across many legacy templates (documented as hydration-hostile),
    // so per-node fixes are whack-a-mole. We keep SSR (first paint + OG/SEO) but let the
    // client bootstrap FRESH over the server HTML instead of hydrating — stable, no walker,
    // no crash. Re-enable (with the transfer cache below) once the templates are hydration-
    // clean as a dedicated effort.
    //   provideClientHydration(withHttpTransferCacheOptions({ includePostRequests: false })),
    // withFetch only (dropped withXhr): the Fetch backend registers PendingTasks that
    // Angular SSR awaits, so data-driven routes (article OG via Meta) render with real
    // data server-side instead of the shell/defaults. (#52 / NG02801)
    provideHttpClient(withFetch(), withInterceptorsFromDi())
  ]
};

// Browser-ONLY Firebase providers. Added in main.ts (browser bootstrap) but NOT in
// main.server.ts, so the firebase app is never created server-side. On the server
// AngularFireAuth / AngularFireDatabase / FirebaseServiceService are provided as inert
// stubs from app.config.server.ts. This is what makes SSR safe with @angular/fire
// compat (whose Auth initialises a browser-only popup resolver on app creation). (#52)
export const firebaseProviders = [
  importProvidersFrom(
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireDatabaseModule
  ),
  AngularFireAuth,
  FirebaseServiceService
];
